import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import AgoraChat from 'agora-chat';
import axios from 'axios';
import { useMeetingState } from './MeetingStateContext';

const AgoraChatContext = createContext(null);

export function AgoraChatProvider({ children, meetingId, userId, userName }) {
  const { chatHistory, meetingStartedAt } = useMeetingState();
  const [messages, setMessages] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [chatroomId, setChatroomId] = useState(null);
  const connRef = useRef(null);
  const mountedRef = useRef(true);
  const chatroomIdRef = useRef(null);
  const chatHistoryRef = useRef(chatHistory);
  // Mirror the server's meetingStartedAt so the init effect can read the
  // latest value without re-running (the effect depends on meetingId/userId).
  const meetingStartedAtRef = useRef(meetingStartedAt);
  const retryCountRef = useRef(0);
  const retryTimerRef = useRef(null);

  // Mutable userName — ref ensures sendMessage always uses the latest name (supports mid-session rename)
  const [chatUserName, setChatUserName] = useState(userName);
  const userNameRef = useRef(userName);
  useEffect(() => {
    userNameRef.current = userName;
    setChatUserName(userName);
  }, [userName]);
  // Also keep ref in sync when setChatUserName is called directly
  useEffect(() => {
    userNameRef.current = chatUserName;
  }, [chatUserName]);

  const appKey = process.env.REACT_APP_AGORA_CHAT_APP_KEY;

  // Keep chatHistory ref in sync
  useEffect(() => { chatHistoryRef.current = chatHistory; }, [chatHistory]);
  // Keep meetingStartedAt ref in sync (arrives asynchronously via REST snapshot)
  useEffect(() => { meetingStartedAtRef.current = meetingStartedAt; }, [meetingStartedAt]);

  // Map Agora Chat message to our standard format
  // Use ext.consistentId if available (set by sender), otherwise generate from userId+timestamp
  const mapMessage = useCallback((msg, isLocal = false) => {
    const ts = msg.ext?.ts || msg.time || Date.now();
    const from = msg.from;
    const consistentId = msg.ext?.consistentId || `msg-${from}-${ts}`;
    return {
      id: consistentId,
      text: msg.msg || msg.data || '',
      userId: from,
      userName: msg.ext?.userName || `User ${from}`,
      timestamp: ts,
      isLocal,
      isPrivate: msg.chatType === 'singleChat',
      image: msg.type === 'img' ? msg.url : (msg.ext?.image || null),
      file: msg.type === 'file' ? { name: msg.filename, size: msg.file_length, url: msg.url } : (msg.ext?.file || null),
      replyTo: msg.ext?.replyTo || null,
    };
  }, []);

  // Track when this session started so we can filter out old chat history
  const sessionStartRef = useRef(Date.now());

  useEffect(() => {
    if (!meetingId || !userId || !appKey) return;
    mountedRef.current = true;
    retryCountRef.current = 0;
    sessionStartRef.current = Date.now();
    setMessages([]); // Clear any stale messages from previous session

    const init = async () => {
      try {
        // Get chat token from backend
        const tokenRes = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/chat-token`, {
          userId: String(userId),
        });
        const { token } = tokenRes.data;

        if (!mountedRef.current) return;

        // Create connection
        const conn = new AgoraChat.connection({ appKey });
        connRef.current = conn;

        // Wait for onConnected before proceeding
        const connectedPromise = new Promise((resolve, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Connection timeout after 15s'));
          }, 15000);

          conn.addEventHandler('chatPanel', {
            onConnected: () => {
              clearTimeout(timeout);
              if (mountedRef.current) setIsConnected(true);
              console.log('[AgoraChat] Connected');
              resolve();
            },
            onDisconnected: () => {
              if (mountedRef.current) setIsConnected(false);
              console.log('[AgoraChat] Disconnected');
            },
            onTextMessage: (msg) => {
              console.log('[AgoraChat] Received text:', { from: msg.from, to: msg.to, chatroom: chatroomIdRef.current, id: msg.ext?.consistentId, text: msg.msg?.substring(0, 30) });
              if (!mountedRef.current || msg.from === String(userId)) return;
              // Filter out messages from other chatrooms (prevents cross-room leakage)
              if (msg.chatType === 'chatRoom' && msg.to !== chatroomIdRef.current) {
                console.warn('[AgoraChat] Dropping message from wrong chatroom:', msg.to, 'expected:', chatroomIdRef.current);
                return;
              }
              setMessages(prev => [...prev, mapMessage(msg)]);
            },
            onImageMessage: (msg) => {
              if (!mountedRef.current || msg.from === String(userId)) return;
              if (msg.chatType === 'chatRoom' && msg.to !== chatroomIdRef.current) return;
              setMessages(prev => [...prev, mapMessage(msg)]);
            },
            onFileMessage: (msg) => {
              if (!mountedRef.current || msg.from === String(userId)) return;
              if (msg.chatType === 'chatRoom' && msg.to !== chatroomIdRef.current) return;
              setMessages(prev => [...prev, mapMessage(msg)]);
            },
            onTokenWillExpire: async () => {
              console.log('[AgoraChat] Token expiring, renewing...');
              try {
                const res = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/chat-token`, {
                  userId: String(userId),
                });
                if (connRef.current) {
                  await connRef.current.renewToken(res.data.token);
                }
              } catch (e) {
                console.error('[AgoraChat] Token renewal failed:', e);
              }
            },
            onError: (error) => {
              console.error('[AgoraChat] Error:', error);
            },
          });
        });

        // Login and wait for connected callback
        conn.open({ user: String(userId), accessToken: token });
        await connectedPromise;
        console.log('[AgoraChat] Logged in as', userId);

        if (!mountedRef.current) return;

        // Create or join chatroom
        let roomId;
        try {
          const roomRes = await axios.post(`${process.env.REACT_APP_BASE_URL}/agora/chat-room`, {
            meetingId,
            hostUserId: userId,
          });
          roomId = roomRes.data.chatroomId;
        } catch (e) {
          console.warn('[AgoraChat] Could not create chatroom:', e.message);
        }

        if (roomId && mountedRef.current) {
          // Leave any previously joined chatrooms to prevent cross-room message leakage
          try {
            const joinedRooms = await conn.getChatRooms({ pageNum: 1, pageSize: 50 });
            const currentRooms = joinedRooms?.data || [];
            for (const room of currentRooms) {
              if (room.id && room.id !== roomId) {
                console.log('[AgoraChat] Leaving stale chatroom:', room.id);
                await conn.leaveChatRoom({ roomId: room.id }).catch(() => {});
              }
            }
          } catch (e) {
            // Non-fatal — proceed with joining
            console.warn('[AgoraChat] Could not check joined rooms:', e.message);
          }

          try {
            await conn.joinChatRoom({ roomId });
          } catch (e) {
            // May already be in the room
            console.warn('[AgoraChat] Join chatroom:', e.message || e);
          }
          chatroomIdRef.current = roomId;
          setChatroomId(roomId);

          // Fetch history for late joiners — filter out messages from previous sessions.
          // Anchor on the server's meetingStartedAt (when the first participant
          // opened the meeting) so late-joining hosts see pre-join guest chat.
          // Fall back to a 30-minute window before this client's init if the
          // REST snapshot hasn't populated meetingStartedAt yet (bounded because
          // Agora chatrooms are purged across sessions via leaveChatRoom).
          const anchor = meetingStartedAtRef.current || (sessionStartRef.current - 30 * 60 * 1000);
          const sessionCutoff = anchor - 2 * 60 * 1000;
          try {
            const history = await conn.getHistoryMessages({
              targetId: roomId,
              chatType: 'chatRoom',
              pageSize: 50,
            });
            console.log('[AgoraChat] History response:', history?.messages?.length || 0, 'messages');
            if (history?.messages?.length > 0 && mountedRef.current) {
              const parsed = history.messages.map(m => mapMessage(m)).filter(Boolean);
              const currentSession = parsed.filter(m => m.timestamp >= sessionCutoff);
              console.log('[AgoraChat] Filtered history:', parsed.length, 'total,', currentSession.length, 'from current session');
              if (currentSession.length > 0) {
                setMessages(currentSession);
              }
            }
          } catch (e) {
            console.warn('[AgoraChat] History fetch failed:', e.message || e);
          }

          // Fallback: seed from meeting state if Agora Chat history was empty
          if (mountedRef.current) {
            setMessages(prev => {
              if (prev.length === 0 && chatHistoryRef.current.length > 0) {
                const filtered = chatHistoryRef.current.filter(m => (m.timestamp || 0) >= sessionCutoff);
                if (filtered.length > 0) {
                  console.log('[AgoraChat] Seeding', filtered.length, 'messages from meeting state (filtered from', chatHistoryRef.current.length, ')');
                  return [...filtered];
                }
              }
              return prev;
            });
          }

          console.log('[AgoraChat] Joined chatroom', roomId);
        }

        // Reset retry count on successful init
        retryCountRef.current = 0;
      } catch (error) {
        console.error('[AgoraChat] Init failed:', error);
        // Retry up to 3 times with 5-second delay
        if (mountedRef.current && retryCountRef.current < 3) {
          retryCountRef.current += 1;
          console.log(`[AgoraChat] Retrying init (attempt ${retryCountRef.current}/3) in 5s...`);
          retryTimerRef.current = setTimeout(() => {
            if (mountedRef.current) init();
          }, 5000);
        } else if (mountedRef.current) {
          console.error('[AgoraChat] All retries exhausted — chat will use Socket.IO fallback');
        }
      }
    };

    init();

    return () => {
      mountedRef.current = false;
      if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
      const conn = connRef.current;
      const roomId = chatroomIdRef.current;
      if (conn) {
        if (roomId) conn.leaveChatRoom({ roomId }).catch(() => {});
        conn.close();
        connRef.current = null;
      }
    };
  }, [meetingId, userId, appKey, mapMessage]);

  // Send message to chatroom
  const sendMessage = useCallback(async (text, attachments = null) => {
    if (!connRef.current || !chatroomIdRef.current) return false;
    if (!text?.trim() && !attachments) return false;

    try {
      const timestamp = Date.now();
      const consistentId = `msg-${userId}-${timestamp}`;
      const currentName = userNameRef.current || `User ${userId}`;
      const msg = AgoraChat.message.create({
        chatType: 'chatRoom',
        type: 'txt',
        to: chatroomIdRef.current,
        msg: text?.trim() || '',
        ext: {
          userName: currentName,
          consistentId,
          ts: timestamp,
          image: attachments?.image || null,
          file: attachments?.file || null,
          replyTo: attachments?.replyTo || null,
        },
      });

      await connRef.current.send(msg);
      console.log('[AgoraChat] Sent:', { id: consistentId, to: chatroomIdRef.current, text: text?.substring(0, 30) });
      setMessages(prev => [...prev, {
        id: consistentId,
        text: text?.trim() || '',
        userId: String(userId),
        userName: currentName,
        timestamp,
        isLocal: true,
        image: attachments?.image || null,
        file: attachments?.file || null,
        replyTo: attachments?.replyTo || null,
      }]);

      return true;
    } catch (error) {
      console.error('[AgoraChat] Send failed:', error);
      return false;
    }
  }, [userId]);

  // Send private message
  const sendPrivateMessage = useCallback(async (peerId, text) => {
    if (!connRef.current || !text?.trim()) return false;
    try {
      const timestamp = Date.now();
      const consistentId = `msg-${userId}-${timestamp}`;
      const msg = AgoraChat.message.create({
        chatType: 'singleChat',
        type: 'txt',
        to: String(peerId),
        msg: text.trim(),
        ext: {
          userName: userNameRef.current || `User ${userId}`,
          consistentId,
          ts: timestamp,
        },
      });
      await connRef.current.send(msg);
      return true;
    } catch (error) {
      console.error('[AgoraChat] Private send failed:', error);
      return false;
    }
  }, [userId]);

  // connectionFailed is true when all retries are exhausted and still not connected
  const connectionFailed = !isConnected && retryCountRef.current >= 3;

  const value = { messages, sendMessage, sendPrivateMessage, isConnected, chatroomId, connectionFailed, setChatUserName };

  return (
    <AgoraChatContext.Provider value={value}>
      {children}
    </AgoraChatContext.Provider>
  );
}

export function useAgoraChat() {
  return useContext(AgoraChatContext) || {
    messages: [],
    sendMessage: async () => false,
    sendPrivateMessage: async () => false,
    isConnected: false,
    chatroomId: null,
    connectionFailed: false,
  };
}

export default AgoraChatContext;
